import AddReactionOutlinedIcon from "@mui/icons-material/AddReactionOutlined";
import { Avatar, Box, Stack, Typography } from "@mui/material";
import sampleProfileImage from "assets/images/sample.png";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import TooltipDark from "utils/TooltipDark";
import EmojiBox from "./EmojiBox";
import UserTag from "./UserTag";
import WinWireReaction from "./WinWireReaction";
import WinWireReplyEditInput from "./WinWireReplyEditInput";

const WinWireReply = ({
  user,
  reply,
  sortReactions,
  setIsReplyHovered,
  handleOpenEmojiPopover,
  handleDispatchReaction,
  handleDeleteReply,
  handleEditReply,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(reply.content);
  const sortedReactions = sortReactions(reply.WinWireReactions);
  const [taggedUsers, setTaggedUsers] = useState([]);

  // For displaying the message
  const transformedContent = useMemo(() => {
    if (!reply.content) return [];

    const userTags = reply.WinWireUserTags.map((tag) => tag.UserCompaniesLink);

    return reply.content.split(/(@\[\[\d+\]\])/g).map((part) => {
      const match = part.match(/^@\[\[(\d+)\]\]$/);
      if (match) {
        const uclId = +match[1];
        const userTag = userTags.find((tag) => tag.id === uclId);
        if (userTag) {
          return <UserTag key={userTag.id} userTag={userTag} />;
        }
      }

      const textKey = `text-${Math.random().toString(36).slice(2, 11)}`;
      return <span key={textKey}>{part}</span>;
    });
  }, [reply]);

  // For formatting the editable message
  useEffect(() => {
    if (reply?.WinWireUserTags) {
      const tags = reply.WinWireUserTags.map((tag) => tag.UserCompaniesLink);

      setTaggedUsers(tags);

      // Replace ucl IDs with names in content
      if (reply.content) {
        const updatedContent = tags.reduce((content, ucl) => {
          const regex = new RegExp(`@\\[\\[${ucl.id}\\]\\]`, "g");
          return content.replace(regex, `@${ucl.name}`);
        }, reply.content);
        setInputValue(updatedContent);
      }
    }
  }, [reply]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    setIsReplyHovered(true);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
    setIsReplyHovered(false);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleDeleteClick = () => {
    handleDeleteReply(reply.id);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setInputValue(reply.content);
  };

  const handleSubmitEdit = () => {
    // transform the text to add uclIds as replacements
    let content = inputValue;
    taggedUsers.forEach((ucl) => {
      const regex = new RegExp(`@${ucl.name}`, "g");
      content = content.replace(regex, `@[[${ucl.id}]]`);
    });

    handleEditReply(
      reply.id,
      content,
      taggedUsers.map((ucl) => ucl.id)
    );
    setIsEditing(false);
  };

  const handleReplyReactionClick = (reaction) => {
    handleDispatchReaction(reaction, reply);
  };

  const renderReaction = (reaction, count) => {
    const doesUserHaveReaction = reply.WinWireReactions.some(
      (r) => r.reaction === reaction && r.FK_user_companies_links_id === user.userCompanyLinkId
    );
    return (
      <WinWireReaction
        key={reaction}
        allReactions={reply.WinWireReactions}
        reaction={reaction}
        count={count}
        userReaction={doesUserHaveReaction}
        handleReactionClick={handleReplyReactionClick}
      />
    );
  };

  return (
    <Box
      key={reply.id}
      className="win-wire-reply"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isHovered && !isEditing && (
        <EmojiBox
          handleReactionClick={handleReplyReactionClick}
          handleOpenEmojiPopover={(event) => handleOpenEmojiPopover(event, reply)}
          showOptions
          handleEditClick={handleEditClick}
          handleDeleteClick={handleDeleteClick}
          isOwner={reply.FK_user_companies_links_id === user.userCompanyLinkId}
        />
      )}
      <Stack direction="row" sx={{ width: "100%" }}>
        <Box pr={1}>
          <Avatar
            variant="circular"
            alt={reply.UserCompaniesLink.name}
            src={reply?.UserCompaniesLink?.profile_picture || sampleProfileImage}
            sx={{ width: "32px", height: "32px" }}
          />
        </Box>
        <Stack sx={{ width: "100%" }}>
          <Stack direction="row">
            <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
              {reply.UserCompaniesLink.name}
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "#888", ml: "0.5rem" }}>
              {moment.tz(reply.createdAt, user.timezone).fromNow()}
            </Typography>
          </Stack>
          <Box>
            {isEditing ? (
              <WinWireReplyEditInput
                replyId={reply.id}
                handleCancelEdit={handleCancelEdit}
                handleSubmitEdit={handleSubmitEdit}
                inputValue={inputValue}
                setInputValue={setInputValue}
                taggedUsers={taggedUsers}
                setTaggedUsers={setTaggedUsers}
              />
            ) : (
              <Box whiteSpace="pre-wrap" pb="0.25rem">
                {transformedContent}
              </Box>
            )}
          </Box>
          {reply.WinWireReactions && reply.WinWireReactions.length ? (
            <Stack direction="row" alignItems="center" flexWrap="wrap">
              {sortedReactions.map(([reaction, count]) => renderReaction(reaction, count))}
              <TooltipDark title="Add reaction" placement="top">
                <Box
                  className="reaction-emoji"
                  onClick={(event) => handleOpenEmojiPopover(event, reply)}
                >
                  <AddReactionOutlinedIcon className="add-reaction-emoji" />
                </Box>
              </TooltipDark>
            </Stack>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
};

export default WinWireReply;
